<template>
    <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
    >
      <template #layoutContent>
        <Card :title="$t('access.historics.title')" containerClass="mb-5 mt-5">
          <template #cardToolbar>
            <div class="d-flex justify-content-end align-items-center">
              <div class="d-flex justify-content-center">
                <ButtonDropdownFilter
                    :search="false"
                    :options="getStatusWithAll"
                    value-field="value"
                    :name-field="locale"
                    v-model="query.status"
                    btnClass="btn-md"
                />
                <DateRangePicker id="Date_range_picker_historics" @update:date="handleRangeDate" />
              </div>
            </div>
          </template>
          <template #cardBody>
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                     id="historics_datatable">
                <thead class="mb-5">
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="min-w-100px text-center">{{ $t('access.historics.activities') }}</th> 
                  <th class="min-w-100px text-center">{{ $t('access.historics.doors') }}</th>
                  <th class="min-w-100px text-center">{{ $t('access.historics.member_staff') }}</th>
                  <th class="min-w-100px text-center">{{ $t('access.historics.status') }} </th>
                  <th class="min-w-100px text-center">{{ $t('access.historics.date_hour') }} </th>
                </tr>
                </thead>
            </table>
          </template>
        </Card>
      </template>
    </SubLayout>
</template>

<script setup>
    import {computed, nextTick, onMounted, ref, watch} from "vue";
    import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
    import {useStore} from "vuex";
    import i18n from "@/plugins/i18n";
    import Ls from "@/services/ls";
    import {BASE_URL} from "@/services/api";
    import {useLocales} from "@/composables/locales";
    import {useHasAbility} from "@/composables/hasAbility";
    import Card from "@/Modules/CRM/components/Card";
    import DateRangePicker from "@/components/DateRangePicker.vue";
    import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
    import { useI18n } from 'vue-i18n'

    const store = useStore()
    const { can } = useHasAbility()
    const {locale, datatableLocaleUrl} = useLocales()
    const { t } = useI18n({ useScope: 'global' })

    const title = ref("menu.access.historics")
    const breadcrumbs = ref([
        {
            name: 'access.historics.title',
            route: 'access-historics'
        }
    ])

    const table = ref()
    const query = ref({
        status: '',
        start_date: '',
        end_date: ''
    })

    const getAccessStatus = computed(() => store.getters['configurations/getAccessStatus'])
    const getStatusWithAll = computed(() => {
        return [{fr: t('access.historics.all_status'), en: t('access.historics.all_status'), value: ''}, ...getAccessStatus.value]
    })

    watch(() => locale.value, (value) => {
        nextTick(() => fetchDatas() )
    }, { deep: true })

    watch(() => query.value.status, (newValue) => {
        table.value.ajax.url(`${BASE_URL}/get/access/history?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
    })

    watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
        table.value.ajax.url(`${BASE_URL}/get/access/history?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
    })

    function formatLogText(history){
        let text = ""
        if(history.status === 'DENIED'){
            text =  t('access.historics.denied')
        }else if(history.status === 'FAILED'){
            text =  t('access.historics.failed')
        }else if(history.status === 'SUCCESS'){
            text =  t('access.historics.success')
        }
        return text + ' ' + history.door?.name
    }

    function getStep(history){
        return Object.values(history.steps).filter(step => step.validate).length + "/" + Object.values(history.steps).length
    }

    function fetchDatas() {
        const AUTH_TOKEN = Ls.get('auth.token')
        const pageLimit = Ls.get('pageLength')

        table.value = $("#historics_datatable").DataTable({
            destroy: true,
            responsive: !0,
            searchDelay: 500,
            processing: !0,
            serverSide: !0,
            pageLength: pageLimit ||  5,
            lengthMenu: [5, 10, 25, 50, 75, 100 ],
            order: [[ 0, 'desc']],
            ajax: {
            url: BASE_URL + `/get/access/history?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
            type: "GET",
            beforeSend: function (request) {
                request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
                request.setRequestHeader("Company", Ls.get('current.company'));
            }
            },
            columns: [
            { data: "history" }, { data: "door_name" }, { data: "user_name" }, { data: "status" }, { data: "created_at" }
            ],
            columnDefs: [
            {
                targets: -5,
                orderable: !1,
                className: "dt-center text-center clickable" ,
                render: function (t, a, e, l) {
                    return `<span class="me-2">${formatLogText(t)}</span><span class="badge badge-circle badge-secondary">${getStep(t)}</span>` ;
                },
            },

            {
                targets: -4,
                orderable: !1,
                className: "dt-center text-center clickable" ,
                render: function (t, a, e, l) {
                return '<span data-id="'+ e.id +'">' + t + "</span>";
                },
            },
            {
                targets: -3,
                order: "desc",
                className: "dt-center text-center clickable" ,
                render: function (t, a, e, l) {
                return '<span data-id="'+ e.id +'">' + t + "</span>";
                },
            },
            {
                targets: -2,
                orderable: false,
                className: "dt-center text-center clickable" ,
                render: function (t, a, e, l) {
                let status = getAccessStatusName(t)
                const locale = i18n.global.locale.value
                let className = status.className
                status = status[locale]
                return '<span class="' + className +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
                },
            },
            {
                targets: -1,
                order: "desc",
                className: "dt-center text-center clickable" ,
                render: function (t, a, e, l) {
                return '<span data-id="'+ e.id +'">' + t + "</span>";
                },
            },
            ],
            language: {
            url: datatableLocaleUrl.value,
            }
        });
    }

    function handleRangeDate (range) {
        query.value.start_date = range.start_date
        query.value.end_date = range.end_date
    }

    function getAccessStatusName(status) {
        const {className, fr, en} = getAccessStatus.value.find(el => el.value === status) || ''
        return {className, fr, en}
    }

    function loadData() {
        Promise.all([
            store.dispatch('configurations/loadAccessStatus'),
            fetchDatas()
        ])
    }

    onMounted( () => {
        loadData()
    })
</script>